import { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown, Menu as MenuIcon } from 'lucide-react'
import { useAuth, getDashboardBusiness } from '@cravo/auth'
import { vendorDashboardApi } from '@cravo/api-service'
import { useTranslation } from '@cravo/i18n'
import { PENDING_REQUESTS_EVENT } from '@/lib/dashboard-events'
import { appLogoUrl, tenantName as _tenantName } from '@/lib/tenant'
import { buildNavGroups, type NavItem } from '@/lib/nav-config'
import { useAiEnabled } from '@/lib/use-ai-enabled'

const APP_LOGO = appLogoUrl()
const APP_NAME = _tenantName()

interface TopNavProps {
  onMenuClick?: () => void
}

/**
 * Horizontal top navigation for the `topbar` layout mode.
 *
 * Layout (desktop):
 *   [Brand]   [Primary inline links]   [Group dropdowns]   [Hamburger? — only mobile]
 *
 * Mobile collapses everything behind the hamburger, reusing the existing
 * `<Sidebar>` drawer; this component only renders the brand row + a menu
 * button there. The page-level `<Header>` (page title, status pill,
 * notifications, profile) renders unchanged below this nav so vendors keep
 * the same per-page chrome regardless of style choice.
 */
export function TopNav({ onMenuClick }: TopNavProps) {
  const { t } = useTranslation()
  const { vendor } = useAuth()
  const location = useLocation()
  const businessName = getDashboardBusiness(vendor)?.businessName || t('sidebar.noBusiness')
  const [pendingCount, setPendingCount] = useState(0)
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const { enabled: aiEnabled } = useAiEnabled()
  const navGroups = useMemo(() => buildNavGroups(t, { aiEnabled }), [t, aiEnabled])

  // Promote each group's "primary" items into the inline strip. The rest of
  // each group becomes a dropdown labelled with the group name. A group with
  // *only* secondary items shows up as a dropdown next to the primaries.
  const inlinePrimaries: NavItem[] = useMemo(
    () => navGroups.flatMap((g) => g.items.filter((i) => i.primary)),
    [navGroups],
  )
  const dropdowns = useMemo(() => {
    return navGroups
      .map((g) => ({
        key: g.key,
        label: g.label,
        items: g.items.filter((i) => !i.primary),
      }))
      .filter((g) => g.items.length > 0)
  }, [navGroups])

  // Reuse the same pending-orders counter the sidebar shows, so the
  // Requests link's badge stays consistent across layout switches.
  useEffect(() => {
    let cancelled = false
    async function hydrate() {
      try {
        const response = await vendorDashboardApi.get()
        if (!cancelled) setPendingCount(response.pendingRequests?.length ?? 0)
      } catch {
        // Best-effort — header keeps the default of 0.
      }
    }
    void hydrate()

    function handlePendingCount(event: Event) {
      const detail = (event as CustomEvent<{ count?: number }>).detail
      if (typeof detail?.count === 'number') setPendingCount(detail.count)
    }
    window.addEventListener(PENDING_REQUESTS_EVENT, handlePendingCount as EventListener)
    return () => {
      cancelled = true
      window.removeEventListener(PENDING_REQUESTS_EVENT, handlePendingCount as EventListener)
    }
  }, [])

  // Close the open dropdown when route changes or user clicks outside.
  useEffect(() => {
    setOpenGroup(null)
  }, [location.pathname])
  useEffect(() => {
    if (!openGroup) return
    function handleClickOutside(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpenGroup(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openGroup])

  // ESC closes any open dropdown — accessibility win, also avoids the
  // visually-stranded menu after focus moves elsewhere via Tab.
  useEffect(() => {
    if (!openGroup) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenGroup(null)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [openGroup])

  return (
    <nav ref={containerRef} className="topnav" aria-label={t('sidebar.partnerSubtitle', { businessName })}>
      <button
        type="button"
        className="topnav-hamburger"
        aria-label={t('common.openMenu')}
        onClick={onMenuClick}
      >
        <MenuIcon size={20} />
      </button>

      <div className="topnav-brand">
        <img src={APP_LOGO} alt={APP_NAME} style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 6 }} />
        <span className="topnav-brand-name">{APP_NAME}</span>
      </div>

      <div className="topnav-items">
        {inlinePrimaries.map((item) => (
          <NavLink
            key={item.key}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) => `topnav-item ${isActive ? 'active' : ''}`}
          >
            <item.icon size={16} />
            <span>{item.label}</span>
            {item.key === 'requests' && pendingCount > 0 && (
              <span className="topnav-badge">{pendingCount}</span>
            )}
          </NavLink>
        ))}

        {dropdowns.map((group) => {
          const isOpen = openGroup === group.key
          // Highlight the dropdown trigger when any of its items is the
          // current route — gives the user the same "you are here" cue as
          // the sidebar variant without expanding the menu.
          const isActiveGroup = group.items.some(
            (i) => location.pathname === i.path || location.pathname.startsWith(i.path + '/'),
          )
          return (
            <div key={group.key} className="topnav-dropdown">
              <button
                type="button"
                className={`topnav-item topnav-dropdown-trigger ${isActiveGroup ? 'active' : ''}`}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                onClick={() => setOpenGroup(isOpen ? null : group.key)}
              >
                <span>{group.label}</span>
                <ChevronDown size={14} style={{ transition: 'transform 120ms', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
              </button>
              {isOpen && (
                <div className="topnav-dropdown-menu" role="menu">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.key}
                      to={item.path}
                      end={item.path === '/'}
                      onClick={() => setOpenGroup(null)}
                      className={({ isActive }) =>
                        `topnav-dropdown-item ${isActive ? 'active' : ''}`
                      }
                      role="menuitem"
                    >
                      <item.icon size={16} />
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </nav>
  )
}
